// components/Journey.jsx
import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactConfetti from 'react-confetti';
import { ThemeContext } from '../context/ThemeContext';
import journeyData from '../data/javascripts/journeyData';
import JourneyCard from '../widgets/JourneyCard'; 

const { experiencesData, educationData } = journeyData;

const Journey = () => {
    const { darkMode } = useContext(ThemeContext);
    const [showConfetti, setShowConfetti] = useState(false);
    const [confettiKey, setConfettiKey] = useState(0);
    const [confettiOpacity, setConfettiOpacity] = useState(1);
    
    // Window size state for Confetti canvas size
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    // Refs and State for Confetti targeting (Card position and size)
    const firstExpCardRef = useRef(null);
    const firstEduCardRef = useRef(null);
    const [expCardDimensions, setExpCardDimensions] = useState({ width: 0, height: 0, x: 0, y: 0 });
    const [eduCardDimensions, setEduCardDimensions] = useState({ width: 0, height: 0, x: 0, y: 0 });

    const confettiTimeoutRef = useRef(null);
    const confettiFadeIntervalRef = useRef(null);

    // --- Provided Theme Classes ---
    const themeClasses = useMemo(() => ({
        sectionWrapper: 'bg-bg-light dark:bg-bg-dark py-20',
        headingText: 'text-primary dark:text-secondary',
        mainButton: 'bg-primary dark:bg-secondary hover:bg-primary/90 dark:hover:bg-secondary/90 text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300',
    }), []);
    // ------------------------------------

    // --- Data Sorting (Most recent first) ---
    const sortedExperiences = experiencesData.slice().sort(
        (a, b) => new Date(b.year.split(' - ')[0]) - new Date(a.year.split(' - ')[0])
    );
    const sortedEducation = educationData.slice().sort(
        (a, b) => new Date(b.year.split(' - ')[0]) - new Date(a.year.split(' - ')[0])
    );
    // --------------------

    const updateCardDimensions = useCallback(() => {
        // Update dimensions for the most recent Experience card
        if (firstExpCardRef.current) {
            const rect = firstExpCardRef.current.getBoundingClientRect();
            setExpCardDimensions({ width: rect.width, height: rect.height, x: rect.left, y: rect.top });
        }
        // Update dimensions for the most recent Education card
        if (firstEduCardRef.current) {
            const rect = firstEduCardRef.current.getBoundingClientRect();
            setEduCardDimensions({ width: rect.width, height: rect.height, x: rect.left, y: rect.top });
        }
    }, []); // No dependencies needed for state setters here

    useEffect(() => {
        const handleResize = () => {
            // Correct usage of setWindowSize
            setWindowSize({ width: window.innerWidth, height: window.innerHeight }); 
            updateCardDimensions();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', updateCardDimensions);
        handleResize(); // Initial call to set dimensions
        
        return () => {
            // Cleanup listeners
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', updateCardDimensions);

            // Cleanup timers on unmount (Best Practice)
            if (confettiFadeIntervalRef.current) clearInterval(confettiFadeIntervalRef.current);
            if (confettiTimeoutRef.current) clearTimeout(confettiTimeoutRef.current);
        };
    }, [updateCardDimensions, setWindowSize]); // Added setWindowSize for completeness

    const triggerConfetti = () => {
        // Clear previous timers
        if (confettiFadeIntervalRef.current) clearInterval(confettiFadeIntervalRef.current);
        if (confettiTimeoutRef.current) clearTimeout(confettiTimeoutRef.current);
        
        // Ensure dimensions are current right before confetti starts (Crucial for position)
        updateCardDimensions(); 

        setConfettiKey(prev => prev + 1); 
        setConfettiOpacity(1);
        setShowConfetti(true);

        // Start fade-out after 3 seconds
        confettiTimeoutRef.current = setTimeout(() => {
            let opacity = 1;
            confettiFadeIntervalRef.current = setInterval(() => {
                opacity -= 0.05;
                if (opacity > 0) {
                    setConfettiOpacity(opacity);
                } else {
                    clearInterval(confettiFadeIntervalRef.current);
                    setShowConfetti(false);
                    setConfettiOpacity(1);
                }
            }, 50);
        }, 3000);
    };

    const handleCardClick = (index, type) => {
        // Only trigger confetti for the most recent card (index 0)
        if (index === 0) {
            triggerConfetti(type);
        }
    };
    
    // --- Render Confetti Logic ---
    const renderConfettiOverlay = (type) => {
        const dimensions = type === 'experience' ? expCardDimensions : eduCardDimensions;
        
        if (!showConfetti || dimensions.width === 0 || dimensions.height === 0) return null;

        return (
            <ReactConfetti
                key={confettiKey}
                // Use window size for the full canvas size
                width={windowSize.width} 
                height={windowSize.height}
                initialPosition={{
                    // Position the emitter at the center of the target card
                    x: dimensions.x + dimensions.width / 2, 
                    y: dimensions.y + dimensions.height / 2
                }}
                run={showConfetti}
                recycle={false}
                numberOfPieces={150}
                gravity={0.2}
                initialVelocityY={5}
                colors={['#FF5252', '#FFD740', '#64FFDA', '#448AFF', '#B388FF']}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    opacity: confettiOpacity,
                    transition: 'opacity 0.1s ease-out',
                    zIndex: 9999,
                    pointerEvents: 'none',
                }}
            />
        );
    };
    
    // Sub-heading classes
    const subHeadingClass = `text-2xl font-semibold text-center mb-6 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`;

    return (
        <section id="journey" className={themeClasses.sectionWrapper}> 
            
            {/* Confetti Overlays */}
            {sortedExperiences.length > 0 && renderConfettiOverlay('experience')}
            {sortedEducation.length > 0 && renderConfettiOverlay('education')}

            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2 className={`text-4xl font-bold mb-3 ${themeClasses.headingText}`}> 
                        Professional Journey
                    </h2>
                </div>
                
                {/* --- Work Experience Section --- */}
                <div className="mb-12">
                    <h3 className={subHeadingClass}>
                        Work Experience
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sortedExperiences.map((exp, index) => (
                            <JourneyCard
                                key={`exp-${index}`}
                                data={exp}
                                type="experience"
                                index={index}
                                darkMode={darkMode}
                                onClick={() => handleCardClick(index, 'experience')}
                                cardRef={index === 0 ? firstExpCardRef : null}
                            />
                        ))}
                    </div>
                </div>

                {/* --- Education Section --- */}
                <div className="mb-8">
                    <h3 className={subHeadingClass}>Education</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sortedEducation.map((edu, index) => (
                            <JourneyCard
                                key={`edu-${index}`}
                                data={edu}
                                type="education"
                                index={index}
                                darkMode={darkMode}
                                onClick={() => handleCardClick(index, 'education')}
                                cardRef={index === 0 ? firstEduCardRef : null}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Journey;